import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import './index.css'
import App from './App.tsx'
import { Dashboard } from './pages/Dashboard'
import { AdminLogin } from './pages/AdminLogin'
import { AdminPanel } from './pages/AdminPanel'
import * as Sentry from '@sentry/react'

// Redirect from Cloud Run URL to canonical domain (drfach.com) so that
// when user goes to Stripe and hits Back, they return to drfach.com instead of run.app
const isRedirectingToCanonical =
  import.meta.env.PROD &&
  import.meta.env.VITE_CANONICAL_ORIGIN &&
  typeof window !== 'undefined' &&
  (() => {
    try {
      const target = new URL(import.meta.env.VITE_CANONICAL_ORIGIN as string)
      if (window.location.origin !== target.origin) {
        window.location.replace(
          target.origin + window.location.pathname + window.location.search
        )
        return true
      }
    } catch {
      /* invalid VITE_CANONICAL_ORIGIN */
    }
    return false
  })()

if (!isRedirectingToCanonical) {

  // Initialize Sentry
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE || 'development', // 'production' or 'development'
      release: import.meta.env.VITE_RELEASE || 'dev',
      dist: import.meta.env.VITE_BUILD_SHA || 'local',
      enabled: !!sentryDsn, // Explicitly enable only when DSN is present
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions will be recorded
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors will be recorded
      // Send default PII data (IP addresses, user agents, etc.)
      sendDefaultPii: true,
    })
    console.log(`✅ Sentry initialized for environment: ${import.meta.env.MODE}`)
  }

  // Wrap App with Sentry Profiler for performance monitoring
  const ProfiledApp = Sentry.withProfiler(App)

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AuthProvider>
        <AdminAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<ProfiledApp />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminPanel />} />
            </Routes>
          </BrowserRouter>
        </AdminAuthProvider>
      </AuthProvider>
    </StrictMode>,
  )
}